import axiosInstance from "./api";
import API_URL from "@/globals/api";
import authHeader from "./auth-header";

/**
 * The signed-in user's Stripe invoices and receipts.
 *
 * A SEPARATE FILE from stripe.service.js on purpose. That module is about
 * creating a session and then navigating away from the panel
 * (`window.location.href = url`); this one is a plain data read whose result is
 * rendered in place. Merging them would put a fetch-then-leave helper and a
 * fetch-then-render helper behind one import and invite the wrong one being
 * used.
 *
 * WHY THERE ARE NO PARAMETERS. The backend derives the customer from the
 * verified token and accepts nothing from the caller — no customer id, no user
 * id, no invoice id. Sending one would be ignored, and adding one here would
 * imply the endpoint honours it, which is exactly the misunderstanding that
 * produced the old `?user_id=` billing-portal hole. If you find yourself
 * wanting to pass an id, the answer is a different endpoint, not a parameter.
 */

/** Long enough for a cold serverless start plus a handful of Stripe round trips. */
const DOCUMENTS_TIMEOUT_MS = 25000;

const isFilledString = (value) => typeof value === "string" && value.trim().length > 0;

/**
 * Normalises one document, keeping only fields the table renders.
 *
 * Unknown/absent URLs become null rather than "" or undefined, so the caller's
 * "is there anything to open?" test is a single truthiness check and cannot be
 * fooled by an empty string that renders as a live-looking button pointing at
 * the current page.
 */
function normalise(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (!isFilledString(raw.stripe_id)) return null;

  return {
    stripeId: raw.stripe_id.trim(),
    invoiceId: isFilledString(raw.invoice_id) ? raw.invoice_id : null,
    number: isFilledString(raw.number) ? raw.number : null,
    hostedInvoiceUrl: isFilledString(raw.hosted_invoice_url) ? raw.hosted_invoice_url : null,
    invoicePdf: isFilledString(raw.invoice_pdf) ? raw.invoice_pdf : null,
    receiptUrl: isFilledString(raw.receipt_url) ? raw.receipt_url : null,
    amount: typeof raw.amount === "number" ? raw.amount : null,
    currency: isFilledString(raw.currency) ? raw.currency.toUpperCase() : null,
    status: isFilledString(raw.status) ? raw.status : null,
    created: isFilledString(raw.created) ? raw.created : null,
  };
}

/**
 * Fetches the documents and returns them keyed by Stripe id.
 *
 * A Map, not an array: the payments table looks a document up per row while
 * rendering, and a linear scan per row over a list that can hold 50 entries is
 * work done for nothing on every keystroke in the search box.
 */
export async function getStripeDocuments() {
  const response = await axiosInstance.get(`${API_URL}/stripe/documents`, {
    timeout: DOCUMENTS_TIMEOUT_MS,
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
    },
  });

  const list = Array.isArray(response?.data) ? response.data : [];
  const byStripeId = new Map();

  list.forEach((raw) => {
    const document = normalise(raw);
    // Documents with no openable link at all are dropped here rather than in
    // the component: a row that cannot produce a URL is indistinguishable from
    // a row with no document, and keeping it would mean the component had two
    // different "nothing to show" cases to reason about.
    if (!document) return;
    if (!document.hostedInvoiceUrl && !document.invoicePdf && !document.receiptUrl) return;
    byStripeId.set(document.stripeId, document);
  });

  return byStripeId;
}

export default { getStripeDocuments };
